import { refreshChatNotificationToken } from '../api/chat'
import {
  getNotificationToken,
  setNotificationToken
} from './store'

export const NOTIFICATION_TOKEN_REFRESH_LEAD_SECONDS = 30 * 60

function decodeBase64Url (value) {
  const normalized = String(value || '')
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
  const binary = window.atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  if (window.TextDecoder) return new window.TextDecoder('utf-8').decode(bytes)
  let escaped = ''
  for (let index = 0; index < bytes.length; index += 1) {
    escaped += `%${bytes[index].toString(16).padStart(2, '0')}`
  }
  return decodeURIComponent(escaped)
}

export function parseNotificationToken (token) {
  if (!token || typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null
  try {
    const payload = JSON.parse(decodeBase64Url(parts[0]))
    if (
      !payload ||
      payload.aud !== 'notification-center' ||
      !Number.isInteger(payload.iat) ||
      !Number.isInteger(payload.exp)
    ) {
      return null
    }
    return payload
  } catch (error) {
    return null
  }
}

export function getNotificationTokenState (token, nowSeconds = Math.floor(Date.now() / 1000)) {
  const payload = parseNotificationToken(token)
  if (!payload) return { valid: false, expired: false, remainingSeconds: 0, payload: null }
  const remainingSeconds = payload.exp - nowSeconds
  return {
    valid: remainingSeconds > 0,
    expired: remainingSeconds <= 0,
    remainingSeconds,
    payload
  }
}

export function notificationTokenRefreshDelay (
  token,
  leadSeconds = NOTIFICATION_TOKEN_REFRESH_LEAD_SECONDS
) {
  const state = getNotificationTokenState(token)
  if (!state.valid) return null
  return Math.max(0, (state.remainingSeconds - leadSeconds) * 1000)
}

function renewalError (message, code, cause) {
  const error = new Error(message)
  error.code = code
  error.cause = cause
  return error
}

export function isNotificationTokenAuthError (error) {
  return Boolean(error && error.code === 'notification_auth_required')
}

export async function renewNotificationToken ({
  force = false,
  leadSeconds = NOTIFICATION_TOKEN_REFRESH_LEAD_SECONDS
} = {}) {
  const token = getNotificationToken() || ''
  const state = getNotificationTokenState(token)
  if (!state.valid) {
    throw renewalError('登录令牌已失效，请重新登录', 'notification_auth_required')
  }
  if (!force && state.remainingSeconds > leadSeconds) {
    return { token, refreshed: false, payload: state.payload }
  }

  let response
  try {
    response = await refreshChatNotificationToken({ token })
  } catch (error) {
    const status = error && error.response ? Number(error.response.status) : 0
    if (status === 401 || status === 403) {
      throw renewalError('登录令牌已失效，请重新登录', 'notification_auth_required', error)
    }
    throw renewalError('暂时无法续期实时连接令牌', 'notification_refresh_unavailable', error)
  }

  const result = response.data || {}
  const code = Number(result.code)
  const refreshedToken = result.data && result.data.notification_token
  if (code === 401 || code === 403) {
    throw renewalError(result.msg || '登录令牌已失效，请重新登录', 'notification_auth_required')
  }
  if (code !== 0 || !refreshedToken) {
    throw renewalError(result.msg || '暂时无法续期实时连接令牌', 'notification_refresh_unavailable')
  }

  const refreshedState = getNotificationTokenState(refreshedToken)
  if (!refreshedState.valid) {
    throw renewalError('服务端返回了无效的实时连接令牌', 'notification_refresh_unavailable')
  }
  setNotificationToken(refreshedToken)
  return { token: refreshedToken, refreshed: true, payload: refreshedState.payload }
}
